import { Capacitor } from "@capacitor/core";
import { supabase } from "@/integrations/supabase/client";

/**
 * Register the device for native push notifications and persist the token to
 * the backend. No-op on web (browser push is not configured).
 */
export async function registerForPushNotifications(userId: string): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    // Web platform — skip silently. Browser push could be added later via VAPID.
    return;
  }

  try {
    // Lazy-load so the web bundle never imports the native plugin.
    const { PushNotifications } = await import("@capacitor/push-notifications");

    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === "prompt") {
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive !== "granted") {
      console.warn("Push notification permission not granted");
      return;
    }

    // Wait for the registration event before resolving.
    await new Promise<void>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error("Push registration timed out"));
      }, 10000);

      PushNotifications.addListener("registration", async (token) => {
        clearTimeout(timeoutId);
        const platform = Capacitor.getPlatform() as "ios" | "android" | "web";
        const { error } = await supabase.from("device_tokens").upsert(
          {
            user_id: userId,
            token: token.value,
            platform,
          },
          { onConflict: "token" }
        );
        if (error) console.error("Failed to save device token:", error);
        resolve();
      });

      PushNotifications.addListener("registrationError", (err) => {
        clearTimeout(timeoutId);
        reject(new Error(err.error));
      });

      PushNotifications.register().catch(reject);
    });

    // Optional: surface foreground notifications via a toast or local UI.
    PushNotifications.addListener("pushNotificationReceived", (notification) => {
      console.log("Push received:", notification);
    });
  } catch (err) {
    console.error("Push notification setup failed:", err);
  }
}
